import React from 'react';
import { CountryCode } from '@dayetopia/types';
import { useLocation } from '@reach/router';
import { GatsbyImage } from 'gatsby-plugin-image';
import { RichText } from 'prismic-reactjs';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import { Typography, TextVariant } from '@components/shared/design/Typography';
import LinkButton from '@components/shared/LinkButton';
import LinkTranslated from '@components/shared/LinkTranslated';
import PrismicLinkSerializer from '@components/shared/PrismicLinkSerializer';
import useDesktop from '@components/shared/useDesktop';
import { BREAKPOINT } from '@constants';
import { ArticleShoppingBanner, ArticleShoppingBannerMobileViewDirection } from '@contracts';
import { replaceText } from '@utils/handleTextReplacement';
import { filterShowInCountries } from '@utils/country';
import { getShoppingBanners } from './data/articleShoppingBanner';

interface Props {
  country: CountryCode;
  details?: ArticleShoppingBanner;
}

export default function ArticleShoppingBannerItem({ country, details }: Props) {
  const location = useLocation();
  const isDesktop = useDesktop(BREAKPOINT.mobileL);
  const shoppingBanners = getShoppingBanners();

  if (!details || !details.banner?.uid) {
    return null;
  }
  const { banner, sb_mobile_view } = details || {};

  const shoppingBannersForCountry = filterShowInCountries(shoppingBanners, country);
  const shoppingBanner = shoppingBannersForCountry.find((b) => {
    return b.uid === banner.uid;
  });
  if (!shoppingBanner) {
    return null;
  }

  const {
    title,
    text,
    button_text,
    button_link,
    main_image_horizontal,
    main_image_vertical,
    background_color,
    background_image_horizontal,
    background_image_vertical
  } = shoppingBanner || {};

  const titleText = replaceText(title?.text, country, location);
  const buttonText = replaceText(button_text, country, location);
  const backgroundColor = background_color;
  const backgroundImageHorizontal = background_image_horizontal?.url;
  const backgroundImageVertical = background_image_vertical?.url;

  const isMobileHorizontal = !isDesktop && sb_mobile_view === ArticleShoppingBannerMobileViewDirection.HORIZONTAL;
  const isMobileVertical = !isDesktop && sb_mobile_view === ArticleShoppingBannerMobileViewDirection.VERTICAL;
  const isHorizontal = isDesktop || isMobileHorizontal;

  const renderTitle = () => {
    if (isMobileHorizontal) {
      return <Typography $variant={TextVariant.BodyBoldLG}>{titleText}</Typography>;
    }
    return <Typography $variant={TextVariant.Heading6}>{titleText}</Typography>;
  };
  const renderText = () => {
    if (isDesktop) {
      return (
        <Text $variant={TextVariant.BodyRegularLG}>
          <RichText render={text?.richText} serializeHyperlink={PrismicLinkSerializer}></RichText>
        </Text>
      );
    }
    return null;
  };
  const renderButton = () => {
    if (isDesktop || isMobileVertical) {
      return (
        <LinkButton to={button_link || ''} targetBlank onClick={(e: React.MouseEvent) => e.stopPropagation()}>
          {buttonText}
        </LinkButton>
      );
    }
    return (
      <LinkStyled to={button_link || ''} targetBlank onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        {buttonText}
      </LinkStyled>
    );
  };
  const renderImage = () => {
    if (isDesktop || isMobileHorizontal) {
      return (
        <>
          {main_image_horizontal && main_image_horizontal.gatsbyImageData ? (
            <ImageContent>
              <GatsbyImage alt={main_image_horizontal.alt || ''} image={main_image_horizontal.gatsbyImageData} loading="lazy" />
            </ImageContent>
          ) : null}
        </>
      );
    }
    return (
      <>
        {main_image_vertical && main_image_vertical.gatsbyImageData ? (
          <ImageContent>
            <GatsbyImage alt={main_image_vertical.alt || ''} image={main_image_vertical.gatsbyImageData} loading="lazy" />
          </ImageContent>
        ) : null}
      </>
    );
  };

  return (
    <Wrapper>
      <Container
        $isHorizontal={isHorizontal}
        $backgroundColor={backgroundColor}
        $backgroundImageSrc={{
          horizontal: backgroundImageHorizontal,
          vertical: backgroundImageVertical
        }}
      >
        <MainContent $isHorizontal={isHorizontal}>
          <TextContainer $isHorizontal={isHorizontal}>
            {renderTitle()}
            {renderText()}
          </TextContainer>
          {renderButton()}
        </MainContent>
        {renderImage()}
      </Container>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

interface ContainerProps {
  $isHorizontal: boolean;
  $backgroundColor?: string | null;
  $backgroundImageSrc?: {
    horizontal?: string | null;
    vertical?: string | null;
  };
}

const Container = styled.div<ContainerProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 12px;
  margin-top: 32px;
  margin-bottom: 56px;

  ${(props) =>
    !props.$isHorizontal &&
    `
    width: 343px;
    flex-direction: column;
    gap: 16px;
  `}

  background: ${(props) => props.$backgroundColor || ''};
  background-size: cover;
  ${(props) =>
    !props.$backgroundColor &&
    `
    background-image: url(${props.$isHorizontal ? props.$backgroundImageSrc?.horizontal : props.$backgroundImageSrc?.vertical});`}
`;

interface MainContentProps {
  $isHorizontal: boolean;
}

const MainContent = styled.div<MainContentProps>`
  width: 60%;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  flex-shrink: 0;

  ${(props) =>
    !props.$isHorizontal &&
    `
    width: 100%;
    align-items: center;
    text-align: center;
  `}

  @media only screen and (min-width: ${BREAKPOINT.laptopL}px) {
    gap: 32px;
  }
`;

interface TextContainerProps {
  $isHorizontal: boolean;
}

const TextContainer = styled.div<TextContainerProps>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
  align-self: stretch;

  ${(props) =>
    !props.$isHorizontal &&
    `
    align-items: center;
    text-align: center;
  `}
`;

const ImageContent = styled.div`
  display: flex;
  align-self: end;

  img {
    border-bottom-right-radius: 12px;
  }
`;

const Text = styled(Typography)`
  > * {
    font-family: BrownStd;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 140%; /* 22.4px */
  }
`;

const LinkStyled = styled(LinkTranslated)`
  color: ${Colors.navyBlue};
  text-align: center;
  font-family: BrownStd;
  font-size: 15px;
  font-style: normal;
  font-weight: 700;
  line-height: 160%; /* 24px */
  text-decoration-line: underline;
  text-underline-offset: 8px;
  text-decoration-thickness: 2px;
`;
